import React from 'react';
import {
  Checkbox,
  Grid,
  Button,
  makeStyles,
  Typography,
  FormControlLabel,
  Divider,
  Dialog,
  DialogTitle,
  IconButton
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { InsightDispatch, iRootState } from '../../redux/store';
import LegalTextMessage from './LegalTextMessage';

type ShareConsentSectionProps = {
  callback: () => void;
};

const useClasses = makeStyles(theme => ({
  checkbox: {
    margin: 0,
    padding: 0
  },
  actionButton: {
    margin: theme.spacing(1)
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.primary.contrastText
  },
  consent: {
    alignContent: 'center',
    margin: theme.spacing(1)
  },
  linkButton: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'underline',
    display: 'inline',
    margin: 0,
    padding: 0,
    textTransform: 'capitalize',
    color: 'blue'
  },
  divider: {
    margin: theme.spacing(2)
  },
  title: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  }
}));

const ShareConsentSection = function ShareConsentSection({
  callback
}: ShareConsentSectionProps): React.ReactElement {
  const classes = useClasses();
  const dispatch = useDispatch<InsightDispatch>();

  const isConsentModalOpen = useSelector(
    (state: iRootState) => state.shareCenter.isConsentModalOpen
  );
  const isConsentAccepted = useSelector(
    (state: iRootState) => state.shareCenter.isConsentAccepted
  );
  const isLegalTextDisplayed = useSelector(
    (state: iRootState) => state.shareCenter.isLegalTextDisplayed
  );

  const onClickTermsAndConditions = function showLegalText(): void {
    dispatch.shareCenter.toggleLegalText();
  };

  const closeConsentModal = function closeConsentModal(): void {
    dispatch.shareCenter.openCloseConsentModal(false);
  };

  const onCheckedConsent = function onCheckedConsent(): void {
    dispatch.shareCenter.toggleConsentAcceptance();
  };

  const consentText1 = 'I confirm I have read the ';
  const consentText2 =
    ' and I consent to sharing the AQ Insight Data with the manufacturer.';
  const consentTextSection = (
    <>
      <Typography>
        {consentText1}
        <Button
          className={classes.linkButton}
          onClick={onClickTermsAndConditions}
        >
          Terms and Conditions
        </Button>
        {consentText2}
      </Typography>
    </>
  );

  const consentSection = (
    <Checkbox
      checked={isConsentAccepted}
      onChange={(): void => onCheckedConsent()}
    />
  );

  return (
    <Dialog open={isConsentModalOpen}>
      <DialogTitle disableTypography className={classes.title}>
        <Typography variant='h6'>Terms & Conditions</Typography>
        <IconButton className={classes.closeButton} onClick={closeConsentModal}>
          <Close />
        </IconButton>
      </DialogTitle>

      {isLegalTextDisplayed && <LegalTextMessage />}
      {isLegalTextDisplayed && <Divider className={classes.divider} />}

      <FormControlLabel
        className={classes.consent}
        labelPlacement='end'
        control={consentSection}
        label={consentTextSection}
      />

      <Grid container direction='column'>
        <Grid item container justifyContent='center'>
          <Button
            disabled={!isConsentAccepted}
            size='small'
            color='inherit'
            variant='outlined'
            className={classes.actionButton}
            onClick={callback}
          >
            Proceed
          </Button>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default ShareConsentSection;
